import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Copy, ExternalLink, Globe, Loader2, CheckCircle2 } from "lucide-react";

export default function RetryCloudflareSetupPanel({ site, reload }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function retry() {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await api.post(`/sites/${site.id}/cloudflare/setup`);
      setResult(r.data);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  const isIpFilter = (error || "").includes("9109") || (error || "").toLowerCase().includes("client ip");

  return (
    <div data-testid="sitedetail-retry-cf-panel" className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="label-tiny text-amber-400">⚠ Cloudflare Setup Incomplete</div>
          <div className="text-sm text-zinc-300 mt-1">
            Domain <span className="mono">{site.domain}</span> was purchased on Namecheap, but the Cloudflare zone wasn&apos;t created.
            Most common cause: <span className="mono text-amber-300">your CF API token has an IP filter that blocks our backend</span>.
          </div>
        </div>
        <button
          data-testid="sitedetail-retry-cf-button"
          onClick={retry}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold whitespace-nowrap"
        >
          {busy ? "Retrying…" : "Retry Cloudflare Setup"}
        </button>
      </div>

      {error && (
        <div className="mt-3 border border-red-500/30 bg-red-500/10 text-red-300 px-3 py-2 rounded-sm text-xs mono">
          {error}
          {isIpFilter && (
            <div className="mt-2 text-zinc-200 leading-relaxed">
              <b className="text-white">How to fix:</b>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Go to <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noreferrer" className="text-blue-300 underline">Cloudflare → Profile → API Tokens</a></li>
                <li>Edit the token you wired into this app</li>
                <li>Under <b>Client IP Address Filtering</b>: either delete the filter entirely, OR add the IP shown in the error above to the allowlist</li>
                <li>Click <b>Continue → Update</b></li>
                <li>Come back here and click <b>Retry Cloudflare Setup</b></li>
              </ol>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="mt-3 border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 px-3 py-2 rounded-sm text-xs mono">
          ✓ Cloudflare zone created · ID <span className="text-white">{result.zone_id}</span>
          {result.nameservers_set_on_namecheap && " · NS pushed to Namecheap"}
          {result.already_configured && " (already configured)"}
        </div>
      )}

      <div className="mt-3 text-[11px] text-zinc-500 mono">
        This runs only the Cloudflare portion (zone create → bootstrap DNS → SSL → email routing → push NS back to Namecheap). It will NOT re-purchase the domain.
      </div>
    </div>
  );
}

export function LinkExistingZone({ site, reload }) {
  const [zones, setZones] = useState(null);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || zones !== null) return;
    api.get("/sites/cloudflare/zones")
      .then(r => setZones(r.data.zones || []))
      .catch(e => setErr(e.response?.data?.detail || e.message));
  }, [open, zones]);

  async function link() {
    if (!picked) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/sites/${site.id}/link-cf-zone`, { domain: picked });
      await reload();
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tiny">Link Existing Cloudflare Zone</div>
          <div className="text-xs text-zinc-500 mt-1">Have the domain already on Cloudflare? Skip the purchase and link it here. Email routing &amp; DNS work immediately.</div>
        </div>
        <button
          onClick={() => setOpen(o => !o)}
          className="text-xs px-3 py-1.5 border border-zinc-700 hover:border-white hover:text-white text-zinc-300 rounded-sm"
        >
          {open ? "Cancel" : (site.domain ? "Re-link" : "Pick zone…")}
        </button>
      </div>
      {open && (
        <div className="mt-4 flex gap-2">
          <select
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className="admin-input flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
          >
            <option value="">{zones === null ? "Loading…" : `Choose from ${zones.length} zone(s)`}</option>
            {(zones || []).map(z => (
              <option key={z.zone_id} value={z.domain}>{z.domain} · {z.status}</option>
            ))}
          </select>
          <button
            onClick={link}
            disabled={busy || !picked}
            className="bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold"
          >
            {busy ? "Linking…" : "Link"}
          </button>
        </div>
      )}
      {err && <div className="mt-3 text-xs text-red-400 mono">{err}</div>}
    </div>
  );
}


export function AttachExternalDomain({ site, reload }) {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  async function attach() {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post(`/sites/${site.id}/domain/attach-external`, { domain: domain.trim().toLowerCase() });
      setResult(r.data);
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  function copyNs(ns) {
    navigator.clipboard.writeText(ns);
    setCopied(ns);
    setTimeout(() => setCopied(""), 1500);
  }

  if (site.domain && !result) {
    return null;
  }

  return (
    <div data-testid="sitedetail-attach-external-panel" className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tiny flex items-center gap-2">
            <Globe size={12} /> Attach External Domain
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Already own the domain at another registrar (e.g. a separate Namecheap account)?
            One-click provisioning of the Cloudflare zone + Worker. You paste 2 nameservers
            into your registrar — we handle the rest.
          </div>
        </div>
        <button
          data-testid="sitedetail-attach-external-toggle"
          onClick={() => setOpen(o => !o)}
          className="text-xs px-3 py-1.5 border border-zinc-700 hover:border-white hover:text-white text-zinc-300 rounded-sm"
        >
          {open ? "Cancel" : "Attach…"}
        </button>
      </div>

      {open && !result && (
        <div className="mt-4 space-y-3">
          <div className="flex gap-2">
            <input
              data-testid="sitedetail-attach-external-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="admin-input flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
            />
            <button
              data-testid="sitedetail-attach-external-submit"
              onClick={attach}
              disabled={busy || !domain.trim()}
              className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Globe size={14} />}
              {busy ? "Attaching…" : "Attach"}
            </button>
          </div>
          <div className="text-[11px] text-zinc-500 mono">
            We&apos;ll: create the CF zone → bootstrap DNS &amp; SSL → attach the Worker for apex + www → return the 2 nameservers for you to paste into your registrar.
          </div>
        </div>
      )}

      {err && (
        <div className="mt-3 border border-red-500/30 bg-red-500/10 text-red-300 px-3 py-2 rounded-sm text-xs mono whitespace-pre-wrap">{err}</div>
      )}

      {result && (
        <div className="mt-4 space-y-3">
          <div className="border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 px-3 py-2 rounded-sm text-xs mono flex items-center gap-2">
            <CheckCircle2 size={14} /> Cloudflare zone {result.zone_already_existed ? "linked" : "created"} for{" "}
            <span className="text-white">{result.domain}</span>
            {result.ssl_enabled && " · SSL enabled"}
            {result.worker_attachment?.apex?.ok && " · Worker attached"}
            {result.email_routing_enabled && " · Email routing live"}
          </div>

          <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4">
            <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm mb-2">
              <ExternalLink size={14} /> Action required: set these nameservers at your registrar
            </div>
            <div className="text-[11px] text-zinc-400 mb-3 mono">
              Log into the account where you bought <span className="text-white">{result.domain}</span> and replace the nameservers with the 2 below. After NS propagation (5–30 min), CF activates the zone and your site goes live.
            </div>
            <div className="space-y-1.5">
              {result.nameservers.map((ns, i) => (
                <div key={ns} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
                  <span className="text-[10px] mono text-zinc-500 w-6">NS{i + 1}</span>
                  <span className="mono text-sm flex-1 text-white" data-testid={`sitedetail-attach-external-ns-${i + 1}`}>{ns}</span>
                  <button
                    onClick={() => copyNs(ns)}
                    className="text-xs px-2 py-1 border border-zinc-700 hover:border-white hover:text-white text-zinc-400 rounded-sm inline-flex items-center gap-1"
                    title="Copy"
                  >
                    {copied === ns ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                    {copied === ns ? "Copied" : "Copy"}
                  </button>
                </div>
              ))}
            </div>
            {result.original_nameservers?.length > 0 && (
              <div className="mt-3 text-[10px] text-zinc-500 mono">
                Existing NS at registrar: {result.original_nameservers.join(", ")}
              </div>
            )}
          </div>

          <ol className="list-decimal ml-5 text-xs text-zinc-300 space-y-1">
            {(result.next_steps || []).map((s) => (<li key={s}>{s}</li>))}
          </ol>
        </div>
      )}
    </div>
  );
}

